import React, { useMemo, useState, useRef, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Block, BlockContent, DecompositionMode, DecompositionSource } from '../../types';
import {
  DecompositionCalculationService,
  DecompositionParams,
  BlockCalculationService,
} from '../../services';
import { allData as ouvragesData, Ouvrage } from '../../../1-bibliotheque-prix/data/ServicesData';
import { fuzzyMatchOuvrages } from '../../../6-challenger-devis/utils/fuzzyMatch';
import { computeBenchmarkStats } from '../../../6-challenger-devis/utils/benchmarkStats';
import { parsePrixString } from '../../../6-challenger-devis/utils/priceParsing';
import {
  RELIABILITY_DOT,
  sourcesToReliability,
  reliabilityTextColor,
  FPLabel,
} from '../atoms';
import SourceSelectionModal from './SourceSelectionModal';

/**
 * DecompositionInlineSubbar — piste 2 (ligne étendue) / modèle A (progressive
 * disclosure). Remplace l'empilement DecompositionMOSummaryBar + Panel par un
 * seul bandeau discret directement éditable, ancré sous la ligne parente.
 *
 * Principes :
 *  - Pas d'accordéon : le bandeau est l'éditeur, pas un résumé cliquable.
 *  - Couleurs quasi-neutres (gris-50) — ni ambre ni violet.
 *  - Mode « fourniture-seule » ↔ « fourni-pose » via un toggle textuel minimaliste.
 *  - Fermeture explicite via × : repasse en mode forfaitaire et réinitialise.
 */
interface DecompositionInlineSubbarProps {
  block: Block;
  params: DecompositionParams;
  onContentChange: (content: BlockContent) => void;
  onClose: () => void;
}

const fmtEuro = (n: number) => BlockCalculationService.formatPrice(n);
const numToInput = (n: number | undefined): string =>
  n === undefined || n === null || Number.isNaN(n) ? '' : String(n);

const DecompositionInlineSubbar: React.FC<DecompositionInlineSubbarProps> = ({
  block, params, onContentChange, onClose,
}) => {
  const mode: DecompositionMode = block.content.decompositionMode ?? 'fourni-pose';
  const isFourniPose = mode === 'fourni-pose';
  const quantite = BlockCalculationService.parseQuantity(block.content.quantite ?? '1');
  const unite = block.content.unite || 'u';

  const breakdown = useMemo(
    () => DecompositionCalculationService.computeBreakdown(block.content, quantite, params),
    [block.content, quantite, params],
  );

  // Dérive un éventuel mismatch d'unité entre la fourniture F sélectionnée en
  // biblio et la ligne parente F+P. Non bloquant — signal visuel uniquement
  // (cf. updates.md « Cohérence d'unité entre fourniture F et ligne F+P »).
  // Lookup par intitulé : on ne déclenche pas le warning pour une saisie libre
  // dont on ne connaît pas l'unité de référence.
  const fournitureUniteMismatch = useMemo(() => {
    const fi = block.content.fournitureIntitule?.trim();
    const lineUnite = block.content.unite?.trim();
    if (!fi || !lineUnite) return null;
    const ouvrage = ouvragesData.find(o => o.intitule === fi && o.type === 'F');
    if (!ouvrage) return null;
    const fUnite = ouvrage.unite?.trim();
    if (!fUnite || fUnite === lineUnite) return null;
    return { fournitureUnite: fUnite, lineUnite };
  }, [block.content.fournitureIntitule, block.content.unite]);

  const updateField = (patch: Partial<BlockContent>) => {
    onContentChange({ ...block.content, ...patch });
  };

  const handleNumberChange = (field: keyof BlockContent, raw: string) => {
    const parsed = raw === '' ? undefined : parseFloat(raw.replace(',', '.'));
    const value = parsed !== undefined && !Number.isNaN(parsed) ? parsed : undefined;
    updateField({ [field]: value, decompositionSource: 'user' as DecompositionSource });
  };

  // Bascule fourniture-seule ↔ fourni-pose. Quand on **active** la MO et que
  // les champs MO sont encore vides, on les pré-remplit depuis les refs de
  // l'ouvrage F sélectionné en biblio (lookup par fournitureIntitule). C'est
  // le pendant du pré-remplissage qui se fait déjà à la sélection d'une
  // fourniture dans le dropdown — l'utilisateur ne devrait pas avoir à
  // re-sélectionner pour récupérer la MO de référence.
  const toggleMO = () => {
    const willActivateMO = !isFourniPose;
    if (!willActivateMO) {
      updateField({ decompositionMode: 'fourniture-seule' });
      return;
    }
    const fi = block.content.fournitureIntitule?.trim();
    const ouvrage = fi ? ouvragesData.find(o => o.intitule === fi && o.type === 'F') : undefined;
    const currentTempsPose = block.content.tempsPose;
    const currentNbHommes = block.content.nbHommes;
    const hasUserMO =
      (typeof currentTempsPose === 'number' && currentTempsPose > 0) ||
      (typeof currentNbHommes === 'number' && currentNbHommes > 0);

    const patch: Partial<BlockContent> = { decompositionMode: 'fourni-pose' };
    if (ouvrage && !hasUserMO) {
      if (ouvrage.tempsPoseRef !== undefined) patch.tempsPose = ouvrage.tempsPoseRef;
      if (ouvrage.nbHommesRef !== undefined) patch.nbHommes = ouvrage.nbHommesRef;
    }
    updateField(patch);
  };

  // PU coût = PU ligne : plus d'override vente (supprimé). La marge, si besoin,
  // se règle via le coefLigne (colonne dédiée sur la ligne parente).
  //
  // Fallback défensif : sur un chiffrage issu d'un SavedChiffrage antérieur,
  // breakdown peut renvoyer undefined — on retombe sur 0 pour éviter les
  // crashs `.toFixed` plus bas.
  const puCout = typeof breakdown.prixUnitaireCout === 'number' && isFinite(breakdown.prixUnitaireCout)
    ? breakdown.prixUnitaireCout
    : 0;

  return (
    <div className="border-b border-gray-200 bg-gray-50/60 pl-10 pr-3 py-1.5 space-y-1">
      {/* Row 1 — Fourniture (toujours visible) */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-[9px] uppercase tracking-wider text-gray-400 font-medium w-14 flex-shrink-0">
          Fourniture
        </span>
        <FournitureInline block={block} onUpdate={updateField} />

        {/* Warning d'unité : fourniture F en biblio ≠ unité ligne. Non bloquant.
            Tooltip stylé (on hover) car l'icône seule est trop petite pour que
            le title natif soit efficace — cf. reliability dot pour le même pattern. */}
        {fournitureUniteMismatch && (
          <span
            className="relative group/unite-mismatch flex items-center flex-shrink-0 text-amber-500 cursor-help"
            title={`Unités incohérentes — fourniture en ${fournitureUniteMismatch.fournitureUnite}, ligne en ${fournitureUniteMismatch.lineUnite}`}
          >
            <ExclamationTriangleIcon className="w-3.5 h-3.5" />
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 opacity-0 group-hover/unite-mismatch:opacity-100 transition-opacity duration-150 z-50">
              <span className="block bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
                <span className="flex items-center gap-1 font-semibold text-amber-300 mb-1">
                  <ExclamationTriangleIcon className="w-3 h-3" />
                  Unités incohérentes
                </span>
                <span className="block text-gray-300 leading-snug">
                  Fourniture en <span className="font-medium text-white">{fournitureUniteMismatch.fournitureUnite}</span>,
                  ligne en <span className="font-medium text-white">{fournitureUniteMismatch.lineUnite}</span>.
                </span>
                <span className="block text-gray-400 text-[10px] mt-1 italic">
                  Non bloquant — vérifiez la cohérence.
                </span>
              </span>
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
            </span>
          </span>
        )}

        {/* Toggle MO : ultra discret (texte uniquement) */}
        <button
          type="button"
          onClick={toggleMO}
          className="ml-auto text-[10px] text-gray-400 hover:text-gray-700 transition-colors"
          title={isFourniPose
            ? 'Désactiver la main d\u2019œuvre \u2014 passer en fourniture seule'
            : 'Ajouter la main d\u2019œuvre \u2014 passer en fourni posé'}
        >
          {isFourniPose ? '− MO' : '+ MO'}
        </button>

        {/* Close × : repli vers forfaitaire */}
        <button
          type="button"
          onClick={onClose}
          className="text-gray-300 hover:text-gray-600 transition-colors"
          title="Fermer la décomposition — revenir au forfaitaire"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Row 2 — MO fields (fourni-pose uniquement) */}
      {isFourniPose && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[9px] uppercase tracking-wider text-gray-400 font-medium w-14 flex-shrink-0">
            Main d'œuvre
          </span>
          <NumberField
            value={block.content.tempsPose}
            onChange={v => handleNumberChange('tempsPose', v)}
            suffix={`h/${unite}`}
            width="w-16"
            placeholder="0"
            tooltip="Temps de pose par unité (heures)"
          />
          <span className="text-gray-300">×</span>
          <NumberField
            value={block.content.nbHommes}
            onChange={v => handleNumberChange('nbHommes', v)}
            suffix=""
            width="w-10"
            step="1"
            placeholder="1"
            tooltip="Taille de l'équipe"
          />
          <span className="text-gray-300">·</span>
          <span className="text-[10px] text-gray-400">Appro</span>
          <NumberField
            value={block.content.tempsAppro}
            onChange={v => handleNumberChange('tempsAppro', v)}
            suffix={`h · défaut ${params.tempsApproDefault}h`}
            width="w-14"
            placeholder={String(params.tempsApproDefault)}
            tooltip="Forfait appro — appliqué une fois par ligne"
          />
        </div>
      )}

      {/* Summary footer — très compact. Toutes les composantes sont en €/unité
          pour que l'addition visuelle soit cohérente avec le Coût final. Appro
          est un forfait ligne, mais affiché amorti sur la qté (cf. tooltip). */}
      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 pl-[72px]">
        <span title={`Coût fourniture — ${fmtEuro(breakdown.coutFourniture)} €/${unite}`}>
          F <span className="tabular-nums text-gray-700">{fmtEuro(breakdown.coutFourniture)} €</span>
        </span>
        {isFourniPose && (
          <>
            <span className="text-gray-300">·</span>
            <span title={`MO = temps × hommes × ${params.tauxHoraire} €/h → ${fmtEuro(breakdown.coutMOUnitaire)} €/${unite}`}>
              MO <span className="tabular-nums text-gray-700">{fmtEuro(breakdown.coutMOUnitaire)} €</span>
            </span>
            <span className="text-gray-300">·</span>
            <span title={`Forfait appro ligne ${fmtEuro(breakdown.coutApproLigne)} € ÷ ${quantite} ${unite} = ${fmtEuro(breakdown.coutApproParUnite)} €/${unite}`}>
              Appro <span className="tabular-nums text-gray-700">{fmtEuro(breakdown.coutApproParUnite)} €</span>
            </span>
          </>
        )}
        <span className="text-gray-300 mx-1">→</span>
        {/* PU coût = PU ligne. La marge éventuelle se règle via le coefLigne
            (colonne dédiée sur la ligne parente). */}
        <span
          className="tabular-nums text-gray-700 font-medium"
          title="PU coût = (F + MO + Appro) / qté — valeur propagée à la ligne"
        >
          Coût <span className="text-gray-800">{fmtEuro(puCout)} €/{unite}</span>
        </span>
      </div>
    </div>
  );
};

// ─── FournitureInline ────────────────────────────────────────────────────────
// Champ fourniture unique : libellé (search DB) + PU avec pastille de
// fiabilité si source=database. Version neutre (pas d'amber) pour la subbar.

interface FournitureInlineProps {
  block: Block;
  onUpdate: (patch: Partial<BlockContent>) => void;
}

const FournitureInline: React.FC<FournitureInlineProps> = ({ block, onUpdate }) => {
  const parentIntitule = block.content.intitule || block.content.titre || '';
  const [intitule, setIntitule] = useState(block.content.fournitureIntitule ?? '');
  const [coutDraft, setCoutDraft] = useState(numToInput(block.content.coutFourniture));
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const skipNextBlurRef = useRef(false);

  useEffect(() => {
    setIntitule(block.content.fournitureIntitule ?? '');
    setCoutDraft(numToInput(block.content.coutFourniture));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  // Champ fourniture de la décomposition → recherche limitée aux ouvrages
  // de type 'F' (fourniture nue, prix d'achat matière). Les entrées F+P
  // (prix vente fourni + posé) ne sont pas pertinentes ici.
  const emptySuggestions = useMemo(() => {
    if (!parentIntitule.trim()) return [];
    return fuzzyMatchOuvrages(parentIntitule, ouvragesData, 'F')
      .filter(r => r.score > 0.2)
      .slice(0, 8)
      .map(r => r.ouvrage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentIntitule]);

  const searchResults = useMemo(() => {
    const q = intitule.trim();
    if (q.length < 2) return emptySuggestions;
    return fuzzyMatchOuvrages(q, ouvragesData, 'F')
      .filter(r => r.score > 0.1)
      .slice(0, 10)
      .map(r => r.ouvrage);
  }, [intitule, emptySuggestions]);

  const handleCoutChange = (raw: string) => {
    const parsed = raw === '' ? undefined : parseFloat(raw.replace(',', '.'));
    const value = parsed !== undefined && !Number.isNaN(parsed) ? parsed : undefined;
    const patch: Partial<BlockContent> = {
      coutFourniture: value,
      decompositionSource: 'user' as DecompositionSource,
    };
    if (block.content.fournitureSource === 'database') {
      patch.fournitureSource = 'user';
    }
    onUpdate(patch);
  };

  const handleSelectOuvrage = (ouvrage: Ouvrage) => {
    const stats = computeBenchmarkStats(ouvrage);
    const avg = stats.avg;
    // Si on a déjà un coutFournitureRef sur l'ouvrage (typiquement sur les
    // ouvrages F) on le privilégie car c'est une valeur curée ; sinon on
    // retombe sur la moyenne des chantiers historiques.
    const coutF = typeof ouvrage.coutFournitureRef === 'number'
      ? ouvrage.coutFournitureRef
      : avg;
    const sources = ouvrage.chantiers
      .map(c => ({
        id: c.id,
        nom: c.nom,
        localisation: c.localisation,
        annee: c.annee,
        fournisseur: c.fournisseur,
        prixUnitaire: parsePrixString(c.prixActualise || c.prixUnitaire),
        active: true,
      }))
      .filter(s => s.prixUnitaire > 0);

    skipNextBlurRef.current = true;
    setIntitule(ouvrage.intitule);
    setCoutDraft(coutF > 0 ? String(coutF) : '');
    setIsOpen(false);
    setSelectedIndex(-1);

    // Pré-remplissage MO : quand l'ouvrage sélectionné (typiquement F) porte
    // des références `tempsPoseRef` / `nbHommesRef`, on les copie dans les
    // champs MO du bloc — mais uniquement s'ils sont encore vides (on ne
    // détruit pas une saisie utilisateur).
    const patch: Partial<BlockContent> = {
      fournitureIntitule: ouvrage.intitule,
      coutFourniture: coutF > 0 ? +coutF.toFixed(2) : undefined,
      fournitureSource: 'database',
      fournitureReliability: sourcesToReliability(sources.length),
      fournitureSources: sources,
      decompositionSource: 'database' as DecompositionSource,
    };

    const currentTempsPose = block.content.tempsPose;
    const currentNbHommes = block.content.nbHommes;
    const hasUserMO =
      (typeof currentTempsPose === 'number' && currentTempsPose > 0) ||
      (typeof currentNbHommes === 'number' && currentNbHommes > 0);

    if (!hasUserMO && (ouvrage.tempsPoseRef !== undefined || ouvrage.nbHommesRef !== undefined)) {
      if (ouvrage.tempsPoseRef !== undefined) {
        patch.tempsPose = ouvrage.tempsPoseRef;
      }
      if (ouvrage.nbHommesRef !== undefined) {
        patch.nbHommes = ouvrage.nbHommesRef;
      }
      // Si la ligne est en mode fourniture-seule, basculer en fourni-pose
      // puisqu'on vient d'alimenter la MO. Sinon laisser le mode courant.
      if (block.content.decompositionMode === 'fourniture-seule') {
        patch.decompositionMode = 'fourni-pose' as DecompositionMode;
      }
    }

    onUpdate(patch);
  };

  const commitIntituleManual = () => {
    if (skipNextBlurRef.current) {
      skipNextBlurRef.current = false;
      return;
    }
    const trimmed = intitule.trim();
    const patch: Partial<BlockContent> = {
      fournitureIntitule: trimmed || undefined,
    };
    if (block.content.fournitureSource === 'database' && trimmed !== block.content.fournitureIntitule) {
      patch.fournitureSource = 'user';
    }
    onUpdate(patch);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && selectedIndex >= 0 && selectedIndex < searchResults.length) {
        handleSelectOuvrage(searchResults[selectedIndex]);
      } else {
        setIsOpen(false);
        e.currentTarget.blur();
      }
      return;
    }
    if (!isOpen || searchResults.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < searchResults.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : searchResults.length - 1);
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const unite = block.content.unite || 'u';
  const hasSources = (block.content.fournitureSources?.length ?? 0) > 0;
  const rel = block.content.fournitureReliability;
  const showDot = !!rel && block.content.fournitureSource === 'database' && hasSources;
  const placeholder = parentIntitule
    ? `Rechercher… (défaut : ${parentIntitule})`
    : 'Rechercher une fourniture…';

  return (
    <>
      {/* Libellé / search */}
      <div className="relative flex-1 min-w-[160px]">
        <input
          type="text"
          value={intitule}
          onChange={e => {
            setIntitule(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => {
            setIsOpen(false);
            commitIntituleManual();
          }, 200)}
          placeholder={placeholder}
          className="w-full px-2 py-0.5 text-xs border border-gray-200 rounded bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
        />

        {isOpen && searchResults.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white shadow-lg max-h-56 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto">
            {searchResults.map((ouvrage, index) => (
              <div
                key={ouvrage.id}
                className={`cursor-pointer select-none py-1.5 pl-3 pr-3 text-xs ${
                  index === selectedIndex ? 'bg-gray-600 text-white' : 'text-gray-900 hover:bg-gray-100'
                }`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelectOuvrage(ouvrage)}
              >
                <div className="flex items-center gap-2">
                  <FPLabel ouvrageType={ouvrage.type} />
                  <span className="font-medium truncate">{ouvrage.intitule}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] rounded-full whitespace-nowrap flex-shrink-0 ${
                    index === selectedIndex ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {BlockCalculationService.formatPrice(computeBenchmarkStats(ouvrage).avg)} €/{ouvrage.unite}
                  </span>
                </div>
                <div className={`text-[10px] mt-0.5 truncate ${index === selectedIndex ? 'text-gray-200' : 'text-gray-500'}`}>
                  {ouvrage.categorie} · {ouvrage.chantiers.length} chantier{ouvrage.chantiers.length > 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        )}
        {isOpen && intitule.length >= 2 && searchResults.length === 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white shadow-lg rounded-md py-1.5 px-3 text-xs text-gray-500 ring-1 ring-black ring-opacity-5">
            Aucun résultat — validez pour saisir « {intitule} » en libre
          </div>
        )}
      </div>

      {/* PU fourniture */}
      {showDot ? (() => {
        const cfg = RELIABILITY_DOT[rel!];
        const textColor = reliabilityTextColor(rel!);
        return (
          <div
            className="relative group/fpu flex items-center gap-1 bg-white border border-gray-200 rounded px-1.5 py-0.5"
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowSourcesModal(true);
            }}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
            <input
              type="number"
              step="0.01"
              min="0"
              value={coutDraft}
              onChange={e => setCoutDraft(e.target.value)}
              onBlur={e => handleCoutChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              title="Clic droit → modifier la sélection des sources"
              className="w-16 text-xs text-right tabular-nums bg-transparent border-0 outline-none focus:ring-0 p-0 text-gray-800 font-medium"
            />
            <span className="text-[9px] text-gray-400 flex-shrink-0">€/{unite}</span>
            <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-52 opacity-0 group-hover/fpu:opacity-100 transition-opacity duration-150 z-50">
              <span className="block bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl">
                <span className={`flex items-center gap-1 mb-1 ${textColor} font-semibold`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </span>
                <span className="text-gray-400 text-[10px] block mb-1.5">{cfg.reason}</span>
                <span className="font-medium text-gray-100 block mb-0.5 truncate">
                  {block.content.fournitureIntitule || parentIntitule}
                </span>
                <span className="text-gray-400 text-[10px] block italic">Clic droit → détails</span>
              </span>
              <span className="absolute top-full right-3 border-4 border-transparent border-t-gray-900" />
            </span>
          </div>
        );
      })() : (
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded px-1.5 py-0.5">
          <input
            type="number"
            step="0.01"
            min="0"
            value={coutDraft}
            placeholder="0,00"
            onChange={e => setCoutDraft(e.target.value)}
            onBlur={e => handleCoutChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="w-16 text-xs text-right tabular-nums bg-transparent border-0 outline-none focus:ring-0 p-0"
          />
          <span className="text-[9px] text-gray-400 flex-shrink-0">€/{unite}</span>
        </div>
      )}

      {showSourcesModal && block.content.fournitureSources && (
        <SourceSelectionModal
          title="Sources du prix fourniture"
          subtitle={block.content.fournitureIntitule || parentIntitule}
          unit={unite}
          sources={block.content.fournitureSources}
          applyLabel="Appliquer au coût fourniture"
          helpText="Décochez les chantiers aberrants pour affiner le coût fourniture moyen"
          onApply={(avg, updatedSources) => {
            const activeCount = updatedSources.filter(s => s.active).length;
            setCoutDraft(String(+avg.toFixed(2)));
            onUpdate({
              coutFourniture: +avg.toFixed(2),
              fournitureSources: updatedSources,
              fournitureReliability: sourcesToReliability(activeCount),
              fournitureSource: 'database',
            });
          }}
          onClose={() => setShowSourcesModal(false)}
        />
      )}
    </>
  );
};

// ─── NumberField ──────────────────────────────────────────────────────────────
// Input compact pour la décomposition : pas de label inline (labels positionnels
// dans la row), juste l'input + suffixe. Neutre.

interface NumberFieldProps {
  value: number | undefined;
  onChange: (raw: string) => void;
  suffix: string;
  width: string;
  placeholder?: string;
  step?: string;
  tooltip?: string;
}

const NumberField: React.FC<NumberFieldProps> = ({
  value, onChange, suffix, width, placeholder, step, tooltip,
}) => (
  <div className="flex items-center gap-1 bg-white border border-gray-200 rounded px-1.5 py-0.5" title={tooltip}>
    <input
      type="number"
      step={step ?? '0.01'}
      min="0"
      value={numToInput(value)}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      className={`${width} text-xs text-right tabular-nums bg-transparent border-0 outline-none focus:ring-0 p-0`}
    />
    {suffix && <span className="text-[9px] text-gray-400 flex-shrink-0">{suffix}</span>}
  </div>
);

export default DecompositionInlineSubbar;
